import {
  advancedFilter,
  Conditions,
  FilterField,
  GetRdProjectTpTicketsQuery,
  GetRdProjectTpTicketsQueryVariables,
  GET_RD_PROJECT_TP_TICKETS,
  keywordFilter,
  RdGroupingTicketOrCollection,
  RdProjectTpTicketsViewFilter,
  RdProjectTpTicketsViewSort,
  TicketGroupingKind,
  useCurrentUserScopeForCurrentCompany,
  useCursorPaginatedQuery,
} from '@neo.tax/shared';

export type RdProjectTpTicket = GetRdProjectTpTicketsQuery['getRdProjectTpTickets']['edges'][number]['node'];

const groupingFilter = (groupingTicketOrCollection: RdGroupingTicketOrCollection): RdProjectTpTicketsViewFilter => {
  if (groupingTicketOrCollection.kind === TicketGroupingKind.GroupingCollection) {
    return {
      groupingTicketingProviderCollectionId: { eq: groupingTicketOrCollection.id },
    };
  }

  if (groupingTicketOrCollection.kind === TicketGroupingKind.GroupingTicket) {
    return {
      groupingTicketingProviderTicketId: { eq: groupingTicketOrCollection.id },
    };
  }

  return {
    groupingTicketingProviderCollectionId: { is: null },
    groupingTicketingProviderTicketId: { is: null },
  };
};

type UseTicketsForRdProjectProps = {
  rdProjectId: string;
  filterConditions?: Conditions<FilterField<RdProjectTpTicketsViewFilter>>;
  searchKeyword?: string | null;
  sorting?: Array<RdProjectTpTicketsViewSort> | RdProjectTpTicketsViewSort;
  numElementsPerFetch?: number;
  groupingTicketOrCollection?: RdGroupingTicketOrCollection;
  fiscalYearId?: string;
  skip?: boolean;
};

export const useTicketsForRdProject = ({
  filterConditions,
  fiscalYearId,
  groupingTicketOrCollection,
  numElementsPerFetch = 30,
  rdProjectId,
  searchKeyword,
  skip = false,
  sorting,
}: UseTicketsForRdProjectProps): {
  hasNextPage: boolean;
  loadMore: () => Promise<void>;
  loading: boolean;
  tickets: RdProjectTpTicket[] | null;
} => {
  const { isPiiRedacted } = useCurrentUserScopeForCurrentCompany();
  const searchFields: Array<'name' | 'identifier' | 'collectionName' | 'contributorName'> = isPiiRedacted
    ? ['name', 'identifier', 'collectionName']
    : ['name', 'identifier', 'contributorName', 'collectionName'];

  const effectiveFiscalYearId = groupingTicketOrCollection?.fiscalYearId ?? fiscalYearId;

  // The grouping query surfaces an epic/collection either because its own name/identifier
  // matched, or because one of its children matched. When the parent itself matches we skip
  // the child keyword filter so the user can browse the whole grouping; when only a child
  // matched we keep the filter so the matching ticket isn't lost in a list of siblings.
  const isInsideRealGrouping =
    groupingTicketOrCollection?.kind === TicketGroupingKind.GroupingTicket ||
    groupingTicketOrCollection?.kind === TicketGroupingKind.GroupingCollection;
  const lowerKeyword = searchKeyword?.toLowerCase() ?? '';
  const parentMatchesKeyword =
    isInsideRealGrouping &&
    !!searchKeyword &&
    (!!groupingTicketOrCollection?.name?.toLowerCase().includes(lowerKeyword) ||
      !!groupingTicketOrCollection?.identifier?.toLowerCase().includes(lowerKeyword));

  const {
    data,
    error,
    hasNextPage,
    loading,
    onLoadMoreItems: loadMore,
  } = useCursorPaginatedQuery<GetRdProjectTpTicketsQuery, GetRdProjectTpTicketsQueryVariables>(
    GET_RD_PROJECT_TP_TICKETS,
    {
      variables: {
        rdProjectId,
        sorting,
        filter: {
          and: [
            keywordFilter(
              searchFields satisfies (keyof RdProjectTpTicket)[],
              parentMatchesKeyword ? null : searchKeyword,
            ),
            advancedFilter<RdProjectTpTicketsViewFilter>(filterConditions),
            ...(groupingTicketOrCollection ? [groupingFilter(groupingTicketOrCollection)] : []),
            ...(effectiveFiscalYearId ? [{ fiscalYearId: { eq: effectiveFiscalYearId } }] : []),
          ],
        },
      },
      skip,
    },
    {
      getConnection: (data) => data.getRdProjectTpTickets,
      numElementsPerFetch,
    },
  );

  const tickets = data?.getRdProjectTpTickets?.edges.map((edge) => edge.node) ?? [];

  if (loading) {
    return { loading: true, tickets, hasNextPage, loadMore };
  }
  if (error) {
    throw error;
  }

  return { loading: false, tickets, hasNextPage, loadMore };
};
